import React from 'react';

import { useAppContext } from 'src/components/app-context';

import { Direction } from '@prisjakt/ui';
import { Clickable } from 'src/components/clickable';
import { FontColor, FontEmphasis, FontWeight } from '@prisjakt/ui/text';
import { IconChevronRight } from '@prisjakt/ui/icon/chevron-right';
import { IconEye } from '@prisjakt/ui/icon/eye';
import { ButtonSize } from '@prisjakt/ui/button';
import { TextButton } from '@prisjakt/ui/button/variants';
import { BodySmallText, TitleSmallText } from '@prisjakt/ui/text/variants';

import { trackUnfeaturedPlaceholderClick } from '../../tracking';

import { UnfeaturedPlaceholderData } from 'src/areas/product/types';

import {
  StyledBodyText,
  StyledButton,
  StyledCardActionArea,
  StyledContentWrapper,
  StyledCountText,
  StyledCTAInner,
  StyledCTAPlaceholder,
  StyledFirstWrapper,
  StyledPriceLabel,
  StyledRowCard,
  StyledSecondWrapper,
} from './styles';
import { MediumUp } from 'src/ui/block';

type Props = {
  count: number;
  price?: number | null;
  showUnfeaturedOffers: () => void;
};

const UnfeaturedOffersPlaceholder = (props: Props) => {
  const { count, price, showUnfeaturedOffers } = props;

  const { t } = useAppContext();

  return (
    <StyledRowCard outlined>
      <StyledCardActionArea
        container={Clickable}
        containerProps={{
          onClick: showUnfeaturedOffers,
          'aria-label': t(
            'product',
            'price-list.unfeatured-placeholder-show-aria-label'
          ),
        }}
      >
        <>
          <StyledContentWrapper>
            <StyledFirstWrapper>
              <BodySmallText emphasis={FontEmphasis.HIGH}>
                <StyledCountText>
                  {t(
                    'product',
                    'price-list.unfeatured-shops-placeholder-count',
                    { count }
                  )}
                </StyledCountText>{' '}
                {t('product', 'price-list.unfeatured-shops-placeholder')}
              </BodySmallText>{' '}
              <StyledBodyText color={FontColor.PRIMARY}>
                {t('product', 'price-list.unfeatured-shops-show-all')}
              </StyledBodyText>
            </StyledFirstWrapper>
            <StyledSecondWrapper>
              {price && (
                <div>
                  <StyledBodyText emphasis={FontEmphasis.LOW}>
                    {t('product', 'price.from-abbr')}{' '}
                  </StyledBodyText>
                  <StyledPriceLabel
                    emphasis={FontEmphasis.LOW}
                    value={price}
                    weight={FontWeight.HEAVIEST}
                  />
                </div>
              )}
            </StyledSecondWrapper>
          </StyledContentWrapper>
          <StyledCTAPlaceholder>
            <StyledCTAInner>
              <MediumUp>
                <TitleSmallText>{t('product', 'store.link')}</TitleSmallText>
              </MediumUp>
              <IconChevronRight />
            </StyledCTAInner>
          </StyledCTAPlaceholder>
          <StyledButton
            placement={Direction.Left}
            title={t('product', 'price-list.unfeatured-shops-show-all')}
          >
            <TextButton
              onlyIcon
              size={ButtonSize.SMALL}
              // Sets to div to prevent non valid button element inside button element
              container="div"
            >
              <IconEye color="maybeBlue700" />
            </TextButton>
          </StyledButton>
        </>
      </StyledCardActionArea>
    </StyledRowCard>
  );
};

export const getOffersWithUnfeaturedPlaceholder = (
  offerRows: JSX.Element[],
  setShowUnfeaturedPlaceholder: (show: boolean) => void,
  unfeaturedPlaceholderData?: UnfeaturedPlaceholderData
) => {
  // If index in offer list for unfeatured placeholder is more than offerRows.length,
  // the placeholder should not be visible.
  // This occurs when offerRows are sliced by numberOfVisiblePrices.
  if (
    !unfeaturedPlaceholderData ||
    unfeaturedPlaceholderData?.index > offerRows.length
  )
    return offerRows;

  const handleOnClick = () => {
    setShowUnfeaturedPlaceholder(false);
    trackUnfeaturedPlaceholderClick(offerRows[0].props.productId);
  };

  return [
    ...offerRows.slice(0, unfeaturedPlaceholderData?.index),
    <UnfeaturedOffersPlaceholder
      key={unfeaturedPlaceholderData.id}
      count={unfeaturedPlaceholderData.count}
      price={unfeaturedPlaceholderData.price}
      showUnfeaturedOffers={handleOnClick}
    />,
    ...offerRows.slice(unfeaturedPlaceholderData?.index),
  ];
};
